import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "../../../styles/globals.scss";
import { resetPassword } from "./services/authServices";
import { showNotification } from "../../../redux/slices/notificationSlice";
import InputField from "../../../components/InputField";
import AuthCard from "../../../components/AuthCard";
import Button from "../../../components/Button";
import FormGroup from "../../../components/FormGroup";
import ErrorText from "../../../components/ErrorText";
import RequiredLabel from "../../../components/RequiredLabel";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPasswordValid = newPassword.length >= 6 && Boolean(token);
  const requiredReason = t("pleaseCompleteRequiredFields", {
    defaultValue: "Please complete all required fields correctly.",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");
    if (!urlToken) {
      setError(t('missingResetToken'));
    } else {
      setToken(urlToken);
    }
  }, [location.search, t]);

  const handleReset = async () => {
    if (isSubmitting) return;
    if (newPassword.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      await resetPassword(token, newPassword);
      dispatch(
        showNotification({ message: t('passwordReset'), type: "success" })
      );
      navigate("/auth/sign-in-with-email");
    } catch (err) {
      setError(t('passwordResetFail'));
      dispatch(
        showNotification({ message: t('passwordResetFail'), type: "error" })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-50 animate-fade-in">
      <AuthCard>
        <h2 className="main-header mb-4 text-center">
          {t("resetYourPassword")}
        </h2>

        <FormGroup>
          <RequiredLabel htmlFor="new-password" className="mb-2">
            {t("password")}
          </RequiredLabel>
          <InputField
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t("enterNewPassword")}
            required
          />
        </FormGroup>
        <ErrorText>{error}</ErrorText>

        <Button
          className="w-full mt-4"
          onClick={handleReset}
          disabled={!isPasswordValid || isSubmitting}
          disabledReason={!isPasswordValid ? requiredReason : ""}
          loading={isSubmitting}
          loadingLabel={t("loading")}
        >
          {t("setNewPassword")}
        </Button>
      </AuthCard>
    </div>
  );
}
